<template>
  <BaseMenu
    :items="labelsList"
    :title="$t('search-filters.labels')"
    :selection="labelsSelected"
    :attach="attach"
    @add-item="addItemToSelection"
    @remove-item="removeItemFromSelection"
    @clear="clearLabels"
    @click-outside="applyImmediately"
    isMultiple
    :search-allowed="hasLabelChoices"
    :is-unclearable="!hasLabelChoices"
    :is-disabled="isDisabled"
    ref="baseLabelsMenu"
  >
    <template v-slot:no-result>
      <div v-if="!hasLabelChoices" class="base-labels-menu__empty">
        <p class="base-labels-menu__empty-text">{{ $t('page.admin-opportunity-labels.empty-choices') }}</p>
        <BaseButton v-if="isManager" is-underline is-small is-transparent is-square @click.native="openLabelsAdmin">
          {{ $t('page.admin-opportunity-labels.manage-link') }}
        </BaseButton>
        <p v-else class="base-labels-menu__empty-text">
          {{ $t('page.admin-opportunity-labels.ask-manager') }}
        </p>
      </div>
      <div v-else class="base-menu__no-result">
        {{ $t('form-auto-complete.no-results') }}
      </div>
    </template>
    <template v-slot:activator>
      <div
        v-if="value.length < 1"
        class="base-labels-menu__add"
        role="button"
        tabindex="0"
        @keydown.enter.prevent="$event.target.click()"
        @keydown.space.prevent="$event.target.click()"
      >
        <BaseIcon name="tag" />
        {{ addLabelText }}
      </div>
      <div v-else class="base-labels-menu__badges">
        <BaseTag
          v-for="(label, index) in displayedLabels"
          :key="index"
          :background="label.color"
          v-tooltip="{ content: changeLabelText, placement: 'top', offset: '8px', delay: 800 }"
        >
          {{ label.name }}
        </BaseTag>
        <v-popover
          v-if="hiddenLabels.length > 0"
          placement="top"
          trigger="hover"
          popoverClass="tender-bottom-nav__nav-tooltip"
        >
          <div class="base-labels-menu__more-labels">+{{ hiddenLabels.length }}</div>
          <template v-slot:popover>
            <div v-for="(label, index) in hiddenLabels" :key="index">{{ label.name }}</div>
          </template>
        </v-popover>
      </div>
    </template>
    <template v-slot:default="{ item }">
      <div class="items">
        <div class="items__badge">
          <BaseTag :background="item.color">{{ item.name }}</BaseTag>
        </div>
      </div>
    </template>
    <template #sheet-choice="{ item }">
      <BaseTag :background="item.color">{{ item.name }}</BaseTag>
    </template>
  </BaseMenu>
</template>

<script>
/*

Locally, we manipulate assigned label objects (with <labelId>) as received from the server,
but <BaseMenu> matches selection on <id>, so we map labelId <-> id (same as BaseAssignation
does for userId).

*/

import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseMenu from '@/components/base/BaseMenu';
import BaseTag from '@/components/base/BaseTag.vue';
import DebounceMixin from '@/mixins/debounce-mixin';
import { mapGetters } from 'vuex';

export default {
  name: 'BaseLabelsMenu',
  components: {
    BaseButton,
    BaseIcon,
    BaseMenu,
    BaseTag,
  },
  mixins: [DebounceMixin],
  props: {
    // All labels of the tenant, selectable in the menu
    items: {
      type: Array,
      required: true,
    },
    // Labels currently assigned to the entity
    value: {
      type: Array,
      default: () => [],
    },
    attach: {
      type: String,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      innerValue: [],
      isDirty: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['isManager']),
    hasLabelChoices() {
      return this.items.length > 0;
    },
    addLabelText() {
      return this.$t('actions.add');
    },
    changeLabelText() {
      return this.$t('actions.change-labels');
    },
    displayedLabels() {
      return this.value;
    },
    hiddenLabels() {
      return this.value.slice(this.displayedLabels.length);
    },
    labelsList() {
      return [...this.items].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    labelsSelected() {
      return this.innerValue.map(({ labelId, ...rest }) => ({
        id: labelId,
        ...rest,
      }));
    },
  },
  methods: {
    mapToAssignedLabel(label) {
      return {
        labelId: label.id,
        name: label.name,
        description: label.description,
        color: label.color,
      };
    },
    addItemToSelection(item) {
      this.isDirty = true;
      const label = this.items.find((label) => label.id === item.id);
      this.innerValue.push(this.mapToAssignedLabel({ ...label }));
      this.updateLabels();
    },
    removeItemFromSelection(item) {
      this.isDirty = true;
      this.innerValue = this.innerValue.filter((label) => label.labelId !== item.id);
      this.updateLabels();
    },
    clearLabels() {
      this.isDirty = true;
      if (this.innerValue.length !== 0) this.innerValue = [];
      this.applyChanges();
      this.closeMenu();
    },
    updateLabels() {
      this.debounce(() => {
        this.applyChanges();
        // A phone's sheet stays open for the next pick, until "Terminé".
        if (!this.$refs.baseLabelsMenu.inSheet) this.closeMenu();
      }, 2500);
    },
    applyImmediately() {
      if (!this.isDirty) return;
      this.debounce(null, 0, true);
      this.applyChanges();
    },
    applyChanges() {
      const labelIds = this.innerValue.map((label) => label.labelId);
      this.$emit('change', labelIds);
      this.$emit('change-labels', this.innerValue);
      this.isDirty = false;
    },
    closeMenu() {
      this.isDirty = false;
      this.$refs.baseLabelsMenu.menu = false;
    },
    openLabelsAdmin() {
      this.closeMenu();
      this.$router.push({ name: 'admin-opportunity-labels' });
    },
  },
  watch: {
    value() {
      this.innerValue = [...this.value];
    },
  },
  created() {
    this.innerValue = [...this.value];
  },
  beforeDestroy() {
    this.innerValue = [];
  },
};
</script>

<style lang="scss">
.base-labels-menu {
  &__add {
    @include inline-add-button;
    gap: 6px;

    .base-icon {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }
  }

  &__badges {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    cursor: pointer;

    .base-tag {
      max-width: 150px;
      cursor: pointer;
      transition: filter 150ms ease-in;

      &:hover {
        filter: brightness(0.94);
      }
    }
    .base-tag__label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    max-width: 240px;
  }

  &__empty-text {
    margin: 0;
    font-size: 14px;
    color: $color-neutral;
  }
}
</style>
